import classNames from 'clsx';
import { groupBy, orderBy, unzip, values } from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Translation from '@swf/components/Translation/Translation';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import type { TranslationTag } from '@swf/core/types/i18n';
import type {
  EventId,
  EventLevelCompoundMarketTemplateColumnClientConfig,
  EventType,
  MarketId,
  MarketLevelCompoundMarketTemplateColumnClientConfig,
  MarketType,
  MarketView,
  MeetingBasedRunner,
  Runner
} from '@swf/generated/types/api/sports';
import { RunnerStatusMarketDetails } from '@swf/generated/types/api/sports';
import { Selection, SportsLink } from '@swf/sports/components';
import Badges from '@swf/sports/components/Badges/Badges';
import EventStatusMessage from '@swf/sports/components/EventStatus/components/EventStatusMessage/EventStatusMessage';
import SportsFormattedTimeZone from '@swf/sports/components/SportsFormattedTimeZone/SportsFormattedTimeZone';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';
import styles from '../../templates/Table/Table.scss';

import {
  TXTCLI_First,
  TXTCLI_Last,
  TXTCLI_Next,
  TXTWCL_Anytime,
  TXTWCL_Game_Lines,
  TXTWCL_Own_goals_do_not_count,
  TXTWCL_Show_More,
  TXTWCL_odds_boost_selections
} from './TableColumnTable.messages.json';

const translationMap: Partial<Record<string, TranslationTag>> = {
  ISP_I18N_ANYTIME: TXTWCL_Anytime,
  ISP_I18N_FIRST: TXTCLI_First,
  ISP_I18N_LAST: TXTCLI_Last,
  ISP_I18N_NEXT: TXTCLI_Next,
  ISP_I18N_OWN_GOALS_DO_NOT_COUNT: TXTWCL_Own_goals_do_not_count,
  ISP_I18N_GAME_LINES: TXTWCL_Game_Lines
};

const SHOW_MORE_THRESHOLD = 10;

type RunnerWithParentIds = (Runner | MeetingBasedRunner) & {
  marketId: MarketId;
  eventId: EventId;
  eventTypeId: EventType;
};

type ClientConfig =
  | MarketLevelCompoundMarketTemplateColumnClientConfig
  | EventLevelCompoundMarketTemplateColumnClientConfig;

export interface Props {
  marketIds: (MarketId | null)[];
  title: ClientConfig['title'];
  information?: ClientConfig['information'];
  marketTypeCodes: ClientConfig['marketTypeCodes'];
  shouldCollapseRunners?: ClientConfig['shouldCollapseRunners'];
  columnTitles?: ClientConfig['columnTitles'];
  shouldHideHeader?: boolean;
  displayLevel?: 'MARKET' | 'EVENT';
}

export const groupRunners = (
  markets: (MarketView | undefined)[],
  marketTypeCodes: MarketType[],
  shouldCollapseRunners?: true
) => {
  const orderedCollapsedMarkets = marketTypeCodes.map((marketType) =>
    markets.find((market) => market?.marketType === marketType)
  );
  const orderedFilteredMarkets = marketTypeCodes
    .map((marketType) => markets.find((market) => market?.marketType === marketType))
    .filter(notEmpty);

  const allRunners = orderedFilteredMarkets.flatMap(({ eventId, eventTypeId, marketId, runners }) =>
    runners.map<RunnerWithParentIds>((runner) => ({
      ...runner,
      eventId,
      eventTypeId,
      marketId
    }))
  );

  const runnerRows = shouldCollapseRunners
    ? unzip(
        orderedCollapsedMarkets.map((market) =>
          allRunners.filter((runner) => runner.marketId === market?.marketId)
        )
      )
    : values(groupBy(allRunners, 'selectionId')).map((runners) =>
        orderedFilteredMarkets.map(({ marketId }) =>
          runners.find((runner) => runner.marketId === marketId)
        )
      );

  return shouldCollapseRunners
    ? runnerRows
    : orderBy(runnerRows, (runners) =>
        // Order by sortPriority of most significant market (first in runners array)
        // If runner is suspended, use infinity to order to ensure it is last in list
        runners[0]?.runnerStatus === RunnerStatusMarketDetails.SUSPENDED
          ? Number.MAX_SAFE_INTEGER
          : runners[0]?.sortPriority
      );
};

const TableColumnTable = ({
  marketIds,
  information,
  marketTypeCodes,
  shouldCollapseRunners,
  columnTitles,
  shouldHideHeader = false,
  displayLevel = 'MARKET',
  title
}: Props) => {
  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const events = useSelector(widgetSelect.events);

  const markets = useMemo(() => {
    const mktIds = new Set(marketIds.filter(notEmpty));
    return Object.values(allMarkets).filter((market) => market && mktIds.has(market.marketId));
  }, [allMarkets, marketIds]);

  const tableRows = groupRunners(markets, marketTypeCodes, shouldCollapseRunners);
  const [firstMarket] = markets.filter(notEmpty);

  const marketHeaders = marketTypeCodes
    .map((code) => Object.values(allMarkets).find((market) => market?.marketType === code))
    .map((market) => {
      if (!market) return undefined;

      return {
        marketType: market.marketType,
        marketName: market.marketName
      };
    });

  const [rowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    tableRows,
    firstMarket.marketId,
    SHOW_MORE_THRESHOLD
  );

  const firstMarketEventId = firstMarket.eventId;
  const event = events[firstMarketEventId];
  const eventType = useSelector((state: RootState) =>
    widgetSelect.eventType(state, event.eventTypeId)
  );
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, event.competitionId)
  );
  const { oddsBoostCount } = event;

  const informationTranslation = information ? translationMap[information] : undefined;
  const titleTranslation = translationMap[title];

  return (
    <>
      <table data-testid="compound-table-column" className={styles.table}>
        {information && (
          <caption className={styles.information}>
            {informationTranslation ? <Translation {...informationTranslation} /> : null}
          </caption>
        )}
        <thead
          aria-hidden={shouldHideHeader}
          className={classNames({ [styles.hidden]: shouldHideHeader })}
          data-testid="market-template-header"
        >
          <tr>
            {titleTranslation && displayLevel === 'EVENT' ? (
              <th>
                <Translation {...titleTranslation} />
              </th>
            ) : (
              <td />
            )}
            {marketHeaders.map((header, i) => {
              if (!header && !shouldCollapseRunners) return null;
              if (!header) return <th scope="col" key={`empty-header-${i}`} />;
              const { marketType, marketName } = header;
              const title = columnTitles && columnTitles[marketType];
              const subTitleTranslation = title ? translationMap[title] : undefined;
              return (
                <th scope="col" key={marketName}>
                  {subTitleTranslation ? (
                    <Translation {...subTitleTranslation} />
                  ) : title ? (
                    title
                  ) : (
                    marketName
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rowsToDisplay.map((row, i) => {
            const { runnerName, eventId, marketId, selectionId } = row.find(Boolean) ?? {};
            const market = allMarkets[marketId ?? ''];

            if (!runnerName || !eventId || !market) return null;

            const rowName = shouldCollapseRunners ? events[eventId].participants?.[i] : runnerName;

            return (
              <tr
                key={`${eventId}-${selectionId}`}
                className={classNames({ [styles.eventTableRow]: displayLevel === 'EVENT' })}
              >
                <th scope="row">
                  {displayLevel === 'EVENT' ? (
                    <SportsLink
                      className={styles.team}
                      alias="sportsEvent"
                      values={{
                        eventTypeSlug: eventType.eventTypeSlug,
                        eventTypeId: eventType.eventTypeId,
                        competitionSlug: competition.competitionSlug,
                        competitionId: competition.competitionId,
                        eventSlug: event.eventSlug,
                        eventId: event.eventId
                      }}
                    >
                      {rowName}
                    </SportsLink>
                  ) : (
                    rowName
                  )}
                </th>
                {row.map((runner, j) => {
                  if (typeof runner === 'undefined') {
                    return (
                      <td key={`cell-${rowName}-${i}-${j}`}>
                        <span className={styles.emptySelection}>-</span>
                      </td>
                    );
                  }

                  const market = markets.find((market) => market?.marketId === runner.marketId);

                  if (!market) return null;

                  return (
                    <td key={`${runner.selectionId}-${j}`}>
                      <Selection
                        competitionId={market.competitionId}
                        eventId={market.eventId}
                        eventTypeId={market.eventTypeId}
                        handicap={runner.handicap}
                        legTypes={market.legTypes}
                        marketId={market.marketId}
                        marketStatus={market.marketStatus}
                        marketType={market.marketType}
                        odds={runner.winRunnerOdds}
                        runnerResultType={runner.resultType}
                        runnerStatus={runner.runnerStatus}
                        selectionId={runner.selectionId}
                        startTime={events[market.eventId].eventStartTime}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {displayLevel === 'EVENT' ? (
          <tfoot>
            <tr>
              {firstMarket.inPlay ? (
                <td className={styles.eventTime}>
                  <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size="XSmall" />
                </td>
              ) : (
                <td className={styles.eventTime}>
                  <SportsFormattedTimeZone date={event.eventStartTime} withTime />
                </td>
              )}

              <td>
                {oddsBoostCount ? (
                  <Badges
                    className={styles.badge}
                    count={oddsBoostCount}
                    iconName="price-boost"
                    iconLabel={formatMessage(TXTWCL_odds_boost_selections)}
                  />
                ) : null}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

export default TableColumnTable;
